import React, { useState, useEffect, useMemo } from "react";
import {
  stripePublicKey,
  fullBlock,
  RequestStatus,
} from "../../../../utils/Constants";
import {
  utcToDate,
  formatDateFromSeconds,
  calculateTimeDifferenceInDays,
  dateDiffInDaysAndHours,
  calculateSectionSizesWithExponent,
} from "../../../../utils/utilities";
import {
  getSubscriptions,
  updateSubscription,
  cancelSubscriptions,
  getPaymentMethods,
  updateDefaultPaymentMethod,
  getInvoices,
  getUpComingInvoice,
  retryInvoices,
  getRollovers,
} from "../../../../redux/thunks/Subscription";
import Plan from "../../../auth/Plan";
import Table from "../../../common/Table";
import Payment from "../../../auth/Payment";
import { useMediaQuery } from "@mui/material";
import { loadStripe } from "@stripe/stripe-js";
import AlertBox from "../../../common/AlertBox";
import CustomModal from "../../../common/Modal";
import Radio from "../../../sub-component/Radio";
import { Elements } from "@stripe/react-stripe-js";
import Button from "../../../sub-component/Button";
import ProgressBar from "../../../common/ProgressBar";
import { useDispatch, useSelector } from "react-redux";
import CustomTooltip from "../../../common/CustomTooltip";
import { getTenant } from "../../../../redux/thunks/Tenant";
import ProcessingIndicator from "../../../common/ProcessingIndicator";
import BillingSettingsAddCardModal from "./BillingSettingsAddCardModal";
import BillingSettingsViewCardModal from "./BillingSettingsViewCardModal";
import { ReactComponent as Check } from "../../../../assets/icons/pricing/tick.svg";
import { ReactComponent as PaymentIcon } from "../../../../assets/icons/pricing/dollar-sign.svg";
import { ReactComponent as AlertIcon } from "../../../../assets/icons/pricing/alert-triangle.svg";
import { ReactComponent as PlusIcon } from "../../../../assets/icons/components/dashboard/plus.svg";
import { ReactComponent as InfoIcon } from "../../../../assets/icons/components/dashboard/info.svg";
import { ReactComponent as VisaIcon } from "../../../../assets/icons/components/dashboard/visa.svg";
import { ReactComponent as DownloadIcon } from "../../../../assets/icons/pricing/download-icon.svg";
import { ReactComponent as ArrowRightAlt } from "../../../../assets/icons/pricing/arrow-right.svg";
// import { ArrowRightAlt } from "@mui/icons-material";
import { ReactComponent as SetingIcon } from "../../../../assets/icons/components/dashboard/settings.svg";

const stripePromise = loadStripe(stripePublicKey);

export default function BillingSettings(props) {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.authReducer);
  const tenants = useSelector((state) => state.tenantReducer);
  const subscription = useSelector((state) => state.subscriptionReducer);

  const expireAt =
    subscription?.user_subscription.length > 0
      ? subscription?.user_subscription[0]?.expires_at
      : null;
  const daysLeft = calculateTimeDifferenceInDays(expireAt);
  const openLeftDaysDisclaimer = sessionStorage.getItem(
    "openLeftDaysDisclaimer",
  );

  const billingTableHeader = [
    {
      id: "date",
      name: "Date",
      type: "string",
      disableSorting: false,
      screens: ["web", "tab", "mini_tab", "mob"],
    },
    {
      id: "amount",
      name: "Amount",
      type: "number",
      disableSorting: false,
      screens: ["web", "tab", "mini_tab", "mob"],
    },
    {
      id: "invoiceStatus",
      name: "Status",
      type: "component",
      disableSorting: true,
      screens: ["web", "tab"],
      enableFilter: true,
      filterName: "status",
      filterOptions: [
        { name: "Paid", value: "paid" },
        { name: "Pending", value: "open" },
        { name: "Cancelled", value: "void" },
        { name: "Draft", value: "draft" },
      ],
    },
    {
      id: "actions",
      name: "Actions",
      type: "component",
      disableSorting: true,
      screens: ["web", "tab", "mini_tab", "mob"],
    },
  ];

  const customSorting = {
    amount: (a, b) => {
      const parseScore = (scoreString) => {
        return parseFloat(scoreString);
      };
      return parseScore(a) - parseScore(b);
    },
    date: (a, b) => {
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateA - dateB;
    },
    status: (a, b) => {
      return a - b;
    },
  };
  const isOnMobileScreen = useMediaQuery("(max-width: 767px)");
  const [plan, setPlan] = useState(null);
  const [selectedNewPlan, setSelectedNewPlan] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [alert, setAlert] = useState(null);
  const [loader, setLoader] = useState(true);
  const [changePlan, setChangePlan] = useState(false);
  const [trigerEvent, setTrigerEvent] = useState(false);
  const [openPlansDialogue, setOpenPlansDialogue] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddCardModalOpen, setIsAddCardModalOpen] = useState(false);
  const [isViewCardModalOpen, setIsViewCardModalOpen] = useState(false);
  const [openCancelPlanDialogue, setOpenCancelPlanDialogue] = useState(false);
  const [openConfirmationDialogue, setOpenConfirmationDialogue] =
    useState(false);
  const [openNotCancelPlanDialogue, setOpenNotCancelPlanDialogue] =
    useState(false);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem("assessmentsRowsPerPage");
    return saved ? parseInt(saved, 10) : 10;
  });
  const [showFeatureComparison, setShowFeatureComparison] = useState(false);

  const handleRowsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    localStorage.setItem("assessmentsRowsPerPage", newLimit);
  };

  function addOneMonthAndFormat() {
    // Get the current date
    const currentDate = new Date();

    // Add 1 month to the current date
    const nextMonthDate = new Date(currentDate);
    nextMonthDate.setMonth(currentDate.getMonth() + 1);

    // Format the date as '20/07/2024'
    const formattedDate = `${nextMonthDate.getDate()}/${String(
      nextMonthDate.getMonth() + 1,
    ).padStart(2, "0")}/${nextMonthDate.getFullYear()}`;

    // Return the formatted date
    return formattedDate;
  }

  function addOneYearAndFormat() {
    // Get the current date
    const currentDate = new Date();

    // Add 1 year to the current date
    const nextYearDate = new Date(currentDate);
    nextYearDate.setFullYear(currentDate.getFullYear() + 1);

    // Format the date as '20/07/2025'
    const formattedDate = `${nextYearDate.getDate()}/${String(
      nextYearDate.getMonth() + 1,
    ).padStart(2, "0")}/${nextYearDate.getFullYear()}`;

    // Return the formatted date
    return formattedDate;
  }

  const handleConfirmPlan = (plan) => {
    setPlan(plan);
    setOpenConfirmationDialogue(true);
  };

  const handleUpdatePlan = (plan) => {
    setSelectedNewPlan(plan);
    setOpenConfirmationDialogue(true);
    setChangePlan(true);
  };

  const handleRetryInvoices = async () => {
    const { payload, meta } = await dispatch(retryInvoices());
    if (payload === "No pending invoice") {
      setAlert(
        <AlertBox
          color="warning"
          description={payload}
          title="Alert"
          isOpen={true}
          onCloseAlert={() => {
            setAlert(null);
          }}
        />,
      );
    } else if (meta?.requestStatus === RequestStatus.REJECTED) {
      setAlert(
        <AlertBox
          color="danger"
          description={payload}
          title="Error"
          isOpen={true}
          onCloseAlert={() => {
            setAlert(null);
          }}
        />,
      );
    } else if (meta?.requestStatus === RequestStatus.FULFILLED) {
      setAlert(
        <AlertBox
          color="success"
          description={"All Pending invoices are updated"}
          title="Success"
          isOpen={true}
          onCloseAlert={() => {
            setAlert(null);
          }}
        />,
      );
      setTrigerEvent(!trigerEvent);
      setLoader(true);
    }
  };

  const getSubscription = async () => {
    await dispatch(
      getSubscriptions({
        expand: "[stripe_product, tenant, subscription_usage]",
      }),
    );
    await dispatch(getRollovers());
    await dispatch(getPaymentMethods());
    await dispatch(getInvoices());
    await dispatch(getUpComingInvoice());
    await dispatch(getTenant({ id: auth?.data?.tenant_id }));
    setLoader(false);
  };

  useEffect(() => {
    getSubscription();
  }, [trigerEvent]);

  useEffect(() => {
    if (subscription?.user_subscription?.length) {
      setPlan(subscription?.user_subscription[0]);
    }
  }, [subscription?.user_subscription]);

  const getOverage = (attempts, usage) => {
    if (usage - attempts > 0) {
      return usage - attempts;
    }
    return 0;
  };

  const getTotalCost = (attempts, usage, cost_per_assessment) => {
    if (getOverage(attempts, usage)) {
      return getOverage(attempts, usage) * (cost_per_assessment / 100);
    } else {
      return 0;
    }
  };

  const rowsData = useMemo(() => {
    if (subscription?.invoices) {
      const statusMap = {
        open: "Pending",
        paid: "Paid",
        void: "Cancelled",
        draft: "Draft",
      };
      return subscription.invoices?.data?.map((invoice) => ({
        date: utcToDate(invoice.created),
        amount: `${
          invoice.status === "paid"
            ? invoice.amount_paid / 100
            : invoice.amount_due / 100
        }$`,
        invoiceStatus: (
          <div
            className={`${
              invoice.status === "paid"
                ? "success-tag"
                : invoice.status === "open"
                  ? "pending-tag"
                  : invoice.status === "void"
                    ? "fail-tag"
                    : "draft-tag"
            }`}
          >
            {statusMap[invoice.status]}
          </div>
        ),
        actions: (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ width: "auto" }}
          >
            <a
              href={invoice.invoice_pdf}
              onClick={async (e) => {
                e.preventDefault();
                setDownloadingInvoiceId(invoice.id);

                const link = document.createElement("a");
                link.href = invoice.invoice_pdf;
                link.download = `invoice-${invoice.id}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                setTimeout(() => setDownloadingInvoiceId(null), 3200);
              }}
              className="d-flex align-items-center"
              style={{
                width: "24px",
                height: "24px",
                justifyContent: "center",
              }}
            >
              {downloadingInvoiceId === invoice.id ? (
                <div style={{ transform: "scale(0.8)" }}>
                  <ProcessingIndicator />
                </div>
              ) : (
                <DownloadIcon />
              )}
            </a>
          </div>
        ),

        ...invoice,
      }));
    }
    return [];
  }, [subscription?.invoices, downloadingInvoiceId]);

  if (loader) {
    return <ProcessingIndicator />;
  }

  // if (!plan) {
  // 	// return <Subscription onConfirmPlan={handleConfirmPlan} />
  // 	return (
  // 		<div className='dashboard-page'>
  // 			<div className='page-header'>
  // 				<span className='headline-medium semibold black-700'>{'Billing Settings'}</span>
  // 			</div>
  // 			<div className='payment-message'>
  // 				<span className='body-1'><span className='medium primary-text'>{`You have ${tenants?.curr_tenant?.free_assessments} free assessment${tenants?.curr_tenant?.free_assessments === 1 ? '' : 's'}. `}</span> Once all your free assessment evaluations get consumed, you won&apos;t be able to invite any new candidates until you set up billing for your account. Any existing candidates with pending assessments will not be able to start them. To prevent this, please select a plan and enter your payment information.</span>
  // 			</div>
  // 			<Plan onConfirmPlan={handleConfirmPlan} />
  // 		</div>
  // 	);
  // }

  const plandetail = [
    {
      rollover_count: parseInt(plan?.stripe_product?.included_attempts),
      rollover_usage: Math.min(
        Math.max(parseInt(plan?.subscription_usage?.assessment_usage), 0),
        plan?.stripe_product?.included_attempts,
      ),
      color: "#4CAF50",
      label: "Plan",
    },
  ];
  const rollovers = subscription?.rollovers;
  const segments =
    auth?.data?.tenant_status === "canceled"
      ? rollovers
      : rollovers?.concat(plandetail);
  const countValues = segments?.map((obj) => obj?.rollover_count);
  const updatedCountValues = calculateSectionSizesWithExponent(countValues);
  const total = updatedCountValues?.reduce((sum, curr) => sum + curr, 0);
  const updatedSegments = segments?.map((item, index) => {
    return {
      ...item,
      width: updatedCountValues[index],
    };
  });

  const currentPlanLabel = plan?.stripe_product?.name
    ? `${plan?.stripe_product?.name} ${
        plan?.stripe_product?.interval === "year" ? "(Yearly)" : "(Monthly)"
      }`
    : "None";

  const nextPaymentAmount =
    auth?.data?.tenant_status === "canceled" ||
    auth?.data?.tenant_status === "free"
      ? "N/A"
      : `$${((subscription?.upcoming_invoice?.amount_due ?? 0) / 100).toFixed(2)}`;

  const nextBillingDate = plan?.expires_at
    ? utcToDate(plan?.expires_at, "short")
    : "N/A";

  const renderCurrentPlanSummary = () => (
    <div className="current-plan-summary">
      <span className="current-plan-summary-title body-1 dark-100">
        Current plan summary
      </span>
      <div className="current-plan-summary-item">
        <span className="current-plan-summary-label body-2 dark-50">
          Current Plan:
        </span>
        <span className="current-plan-summary-badge body-3 white">
          {currentPlanLabel}
        </span>
      </div>
      <div className="current-plan-summary-item">
        <span className="current-plan-summary-label body-2 dark-50">
          Next Payment:
        </span>
        <span className="current-plan-summary-value body-2 dark-100">
          {nextPaymentAmount}
        </span>
      </div>
      <div className="current-plan-summary-item">
        <span className="current-plan-summary-label body-2 dark-50">
          Next billing date
        </span>
        <span className="current-plan-summary-value body-2 dark-100">
          {nextBillingDate}
        </span>
      </div>
    </div>
  );

  return (
    <>
      <CustomModal
        open={openConfirmationDialogue}
        title="Confirm Plan"
        confirmBtnTitle={`${
          changePlan ? "Update Plan" : "Add a payment Method"
        }`}
        varrient="alert-warning-modal"
        processing={isUpdating}
        successAlert={true}
        onConfirm={async () => {
          if (changePlan) {
            setIsUpdating(true);
            const { payload, error } = await dispatch(
              updateSubscription({
                stripe_pricing_id: selectedNewPlan?.id,
                sub_id: subscription?.user_subscription[0]?.id,
                interval: selectedNewPlan?.interval,
              }),
            );
            if (error) {
              setIsUpdating(false);
              setOpenConfirmationDialogue(false);
              setOpenPlansDialogue(false);
              setAlert(
                <AlertBox
                  description={`Your Selected plan can't be added to this Subscription because this is already in use!`}
                  color="danger"
                  title="Error"
                  onCloseAlert={() => {
                    setAlert(null);
                  }}
                />,
              );
            } else {
              await dispatch(
                getSubscriptions({
                  expand: "[stripe_product, tenant, subscription_usage]",
                }),
              );
              setIsUpdating(false);
              setOpenConfirmationDialogue(false);
              setOpenPlansDialogue(false);
              setIsDeleteModalOpen(true);
            }
          } else {
            setOpenConfirmationDialogue(false);
            setOpenPlansDialogue(false);
            setIsAddCardModalOpen(true);
          }
        }}
        handleCloseModal={() => {
          if (!changePlan) {
            setPlan(null);
          }
          setSelectedNewPlan(null);
          setOpenConfirmationDialogue(false);
          setTrigerEvent(!trigerEvent);
        }}
        btnWidth={`${changePlan ? "150px" : "220px"}`}
      >
        <div className="plan-confirm-dialogue">
          {selectedNewPlan && (
            <div className="plan-details">
              <div className="plan-details-header">
                <span className="headline-4 white">
                  {`${selectedNewPlan?.name} ${
                    selectedNewPlan?.interval === "year"
                      ? "(Yearly)"
                      : "(Monthly)"
                  }`}
                </span>
                <span className="headline-1 white">
                  <span className=" headline-1 white">
                    {selectedNewPlan?.price}
                  </span>
                  <span className="body-1 white">
                    {selectedNewPlan?.interval === "year" ? "/year" : "/month"}
                  </span>
                </span>
              </div>
              <div className="plan-details-info">
                {selectedNewPlan?.details?.map((feature, index) => {
                  return (
                    <div key={index} className="plan-details-info-check">
                      <span className="plan-text body-2 dark-100">
                        {feature}
                      </span>
                      <Check />
                    </div>
                  );
                })}
              </div>
              <div className="plan-pricing">
                <div className="w-100 d-flex justify-content-between ">
                  <span className="body-2 dark-100">Total Payment</span>
                  <span className="body-2 dark-100">
                    {selectedNewPlan?.price}
                  </span>
                </div>
                <div className="w-100 d-flex justify-content-between body-2 dark-100">
                  <span>
                    {auth?.data?.tenant_status === "trialing"
                      ? "Payment Due at Trial End"
                      : "Payment Due Today"}
                  </span>
                  <span>{selectedNewPlan?.price}.00</span>
                </div>
                {auth?.data?.tenant_status === "trialing" && (
                  <div>
                    <span className="body-2 dark-50">
                      Next Payment: {selectedNewPlan?.price}.00 on{" "}
                      {selectedNewPlan?.interval === "year"
                        ? addOneYearAndFormat()
                        : addOneMonthAndFormat()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </CustomModal>
      <CustomModal
        open={isDeleteModalOpen}
        varrient="alert-warning-modal"
        successAlert={true}
        title="Plan Updated"
        description={`Your plan has been successfully update to: ${
          plan?.stripe_product?.name
        } ${
          plan?.stripe_product?.interval === "year" ? "(Yearly)" : "(Monthly)"
        }`}
        confirmBtnTitle="Done"
        hideScendBtn={true}
        onConfirm={() => {
          setIsDeleteModalOpen(false);
          setTrigerEvent(!trigerEvent);
          setLoader(true);
        }}
      />
      <CustomModal
        open={openCancelPlanDialogue}
        varrient="alert-warning-modal"
        dangerAlert={true}
        title="Cancel subscription?"
        dangerText={`Are you sure you want to cancel the subscription?`}
        description={`Your assessment will automatically cancel at the end of your current billing cycle. After cancellation, you won't be able to access the platform. Any unused plan assessments will be rolled over at the time of your cancellation. Your rollovers will persist but will be unusable unless you resubscribe.`}
        confirmBtnTitle="Yes, I’m Sure"
        cancelBtnTitle="Not Now"
        btnWidth="150px"
        handleCloseModal={() => {
          setOpenCancelPlanDialogue(false);
        }}
        onConfirm={async () => {
          const { error } = await dispatch(
            cancelSubscriptions({
              sub_id: subscription?.user_subscription[0]?.id,
            }),
          );
          if (error) {
            setOpenNotCancelPlanDialogue(true);
          } else {
            setOpenCancelPlanDialogue(false);
            setTrigerEvent(!trigerEvent);
            setLoader(true);
          }
        }}
      />
      <CustomModal
        open={openNotCancelPlanDialogue}
        varrient="alert-warning-modal"
        warningAlert={true}
        title="You cannot cancel your Subscrption!"
        description={`Please pay your latest pending invoice in order to cancel your subscription.`}
        confirmBtnTitle="Done"
        hideScendBtn={true}
        onConfirm={() => {
          setOpenNotCancelPlanDialogue(false);
          setOpenCancelPlanDialogue(false);
        }}
      />
      <div
        className="dashboard-page hide-scrollbar d-flex flex-column"
        style={{ marginTop: isOnMobileScreen ? "24px" : "0px" }}
      >
        {fullBlock.includes(auth?.data?.tenant_status) && (
          <div className="payment-message">
            <AlertIcon />
            <span className="body-2">
              Missing billing information or failed to charge your payment
              method. Please fix the issue in billing settings or contact your
              Account Owner if you don&apos;t have the necessary permissions.
            </span>
          </div>
        )}
        <div
          className={`page-header d-flex justify-content-between align-items-center w-100 ${
            openPlansDialogue && isOnMobileScreen ? "flex-column" : "flex-row"
          }`}
          style={{
            padding: isOnMobileScreen ? "0px" : "12px",
            gap: openPlansDialogue && isOnMobileScreen ? "16px" : undefined,
            alignItems:
              openPlansDialogue && isOnMobileScreen ? "stretch" : undefined,
          }}
        >
          <span className="headline-3 dark-100">{"Billing Settings"}</span>

          {openPlansDialogue ? (
            renderCurrentPlanSummary()
          ) : isOnMobileScreen ? (
            <div
              style={{
                height: "44px",
                width: "44px",
                background: "#121216",
                borderRadius: "50px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              onClick={() => {
                handleRetryInvoices();
              }}
            >
              {" "}
              <PaymentIcon />
            </div>
          ) : (
            <Button
              btn="ragular-btn"
              size="md"
              varrient="primary"
              preIcon={<PaymentIcon />}
              title={"Pay Latest Pending Invoice"}
              style={{ width: "max-content", background: "#121216" }}
              onClick={() => {
                handleRetryInvoices();
              }}
            />
          )}
        </div>
        <div
          className={`${
            daysLeft < 6 &&
            auth?.data?.tenant_status === "trialing" &&
            !openLeftDaysDisclaimer &&
            "left-day-message-main-box"
          } page-content`}
        >
          {alert}
          {openPlansDialogue ? (
            <>
              <div
                className="plans-modal-container"
                style={{ padding: isOnMobileScreen ? "12px" : "" }}
              >
                <div className="plans-modal w-100">
                  <Plan
                    onConfirmPlan={
                      subscription.user_subscription?.length > 0
                        ? handleUpdatePlan
                        : handleConfirmPlan
                    }
                    handleCloseModal={() => setOpenPlansDialogue(false)}
                    selectedPlan={subscription?.user_subscription[0]}
                    isUpdatePlan={true}
                    processing={subscription?.is_loading}
                    showFeatureComparison={showFeatureComparison}
                    setShowFeatureComparison={setShowFeatureComparison}
                  />
                </div>
              </div>
              <div
                className="mt-4"
                style={{
                  backgroundColor: "white",
                  padding: "16px",
                  borderRadius: "24px",
                }}
              >
                <div className="mb-2 d-flex">
                  <button
                    type="button"
                    onClick={() => setShowFeatureComparison(true)}
                    style={{
                      color: "#7C21FF",
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      display: "flex",
                    }}
                    className="d-flex align-items-center body-1"
                  >
                    Compare all features
                    <ArrowRightAlt
                      style={{
                        width: "24px",
                        height: "24px",
                        marginLeft: "8px",
                      }}
                    />
                  </button>
                </div>
                <div className="d-flex">
                  <span className="d-flex body-1 dark-50">
                    See a side-by-side comparison of all plan limits and
                    features.
                  </span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="billing-settings">
                <div className="billing-settings-detail">
                  <div className="billing-settings-plan">
                    <div className="d-flex flex-column" style={{ gap: "24px" }}>
                      <div className="payment-head">
                        <span className="subtitle-1">Plan Details</span>
                        {auth?.data?.tenant_status === "trialing" && (
                          <div className="trialing-message">
                            <span className="body-3 dark-50">
                              Your free trial will end in{" "}
                              <CustomTooltip
                                text={`${new Date(
                                  formatDateFromSeconds(plan?.expires_at),
                                ).toDateString()} / ${new Date(
                                  formatDateFromSeconds(plan?.expires_at),
                                ).toLocaleTimeString()}`}
                              >
                                <span className="dark-100">
                                  {dateDiffInDaysAndHours(plan?.expires_at)}
                                </span>
                              </CustomTooltip>{" "}
                              After the trial ends, your free assessment
                              evaluations will expire, and you will have to add
                              a payment method to continue using Testfuse.
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="plan-detail-container">
                        <div className="plan-detail-inner-container1">
                          <div className="plan-detail-text">
                            <span className="dark-50 body-2">
                              Current Plan:
                            </span>
                            <span className="dark-100 body-2">
                              {plan?.stripe_product?.name
                                ? `${plan?.stripe_product?.name} ${
                                    plan?.stripe_product?.interval === "year"
                                      ? "(Yearly)"
                                      : "(Monthly)"
                                  }`
                                : "None"}
                            </span>
                          </div>
                          <div className="plan-detail-text">
                            <span className="dark-50 body-2">
                              Next Payment:
                            </span>
                            <span className="body-2 dark-100">
                              {auth?.data?.tenant_status === "canceled" ||
                              auth?.data?.tenant_status === "free"
                                ? "N/A"
                                : `${(
                                    subscription?.upcoming_invoice?.amount_due /
                                    100
                                  ).toFixed(1)} USD`}
                            </span>
                          </div>
                          <div className="plan-detail-text">
                            <span className="body-2 dark-50">
                              {!plan?.cancel_at_period_end &&
                              plan?.status === "trialing"
                                ? "Trial Ends On:"
                                : plan?.cancel_at_period_end
                                  ? "Cancels On:"
                                  : "On:"}
                            </span>
                            <span className="body-2 dark-100">
                              {plan?.expires_at
                                ? utcToDate(plan?.expires_at, "short")
                                : "N/A"}
                            </span>
                          </div>
                        </div>
                        <div className="plan-detail-inner-container2">
                          <div
                            className="h-100 d-flex flex-column align-items-start justify-content-between"
                            style={{ gap: "32px" }}
                          >
                            <ProgressBar
                              varrient="progress-with-segment"
                              text="Assessment Usage Overview:"
                              overage={getOverage(
                                plan?.stripe_product?.included_attempts,
                                plan?.subscription_usage?.assessment_usage,
                              )}
                              usageFreeAssessments={
                                tenants?.curr_tenant?.total_free_assessments -
                                tenants?.curr_tenant?.free_assessments
                              }
                              totalFreeAssessments={
                                tenants?.curr_tenant?.total_free_assessments
                              }
                              tenantStatus={auth?.data?.tenant_status}
                              segments={updatedSegments}
                              total={total}
                            />
                          </div>
                        </div>
                      </div>
                      {/* <div className='plan-detail-container'>
												<div className='plan-detail-inner-container1'>
													<div className='plan-detail-text'>
														<span className='dark-50 body-2'>Current Plan:</span>
														<span className='dark-100 body-2'>
															{plan?.stripe_product?.name ?
																`${plan?.stripe_product?.name} ${plan?.stripe_product?.interval === 'year' ? '(Yearly)' : '(Monthly)'}`
																: 
																'None'
															}
														</span>
													</div>
													<div className='plan-detail-text'>
														<span className='dark-50 body-2'>
														Free Assessment Evaluations:
														</span>
														<span className='body-2 dark-100'>
															{tenants?.curr_tenant?.free_assessments
																? tenants?.curr_tenant?.free_assessments
																: '0'}
														</span>
													</div>
													<div className='plan-detail-text'>
														<span className='dark-50 body-2'>
														Assessments in Plan:
														</span>
														<span className='dark-100 body-2'>
															{auth?.data?.tenant_status === 'trialing' ||
															auth?.data?.tenant_status === 'canceled' ||
															auth?.data?.tenant_status === 'free'
																? '-/-'
																: `${Math.min(
																	Math.max(
																		parseInt(
																			plan?.subscription_usage?.assessment_usage
																		),
																		0
																	),
																	plan?.stripe_product?.included_attempts
																)}/${plan?.stripe_product?.included_attempts}`}
														</span>
													</div>
												</div>
												<div className='plan-detail-inner-container2'>
													<div className='plan-detail-text'>
														<span className='dark-50 body-2'>
														Overage Assessments:
														</span>
														<span className='body-2 dark-100'>
															{getOverage(
																plan?.stripe_product?.included_attempts,
																plan?.subscription_usage?.assessment_usage
															)}
														</span>
													</div>
													<div className='plan-detail-text'>
														<span className='dark-50 body-2'>Next Payment:</span>
														<span className='body-2 dark-100'>
															{auth?.data?.tenant_status === 'canceled' ||
															auth?.data?.tenant_status === 'free'
																? 'N/A'
																: `${(
																	subscription?.upcoming_invoice?.amount_due /
																100
																).toFixed(1)} USD`}
														</span>
													</div>
													<div className='plan-detail-text'>
														<span className='body-2 dark-50'>
															{!plan?.cancel_at_period_end &&
															plan?.status === 'trialing'
																? 'Trial Ends On:'
																: plan?.cancel_at_period_end
																	? 'Cancels On:'
																	: 'On:'}
														</span>
														<span className='body-2 dark-100'>
															{plan?.expires_at
																? utcToDate(plan?.expires_at, 'short')
																: 'N/A'}
														</span>
													</div>
												</div>
											</div> */}
                    </div>
                    {plan?.cancel_at_period_end ? (
                      <div>
                        <span className="body-3 medium">
                          *Note: Your subscription will be automatically
                          cancelled at the end of your billing period{" "}
                          <span
                            className="semibold primary-text"
                            style={{ textDecorationLine: "underline" }}
                          >{`(after ${daysLeft} day${
                            daysLeft === 1 ? "" : "s"
                          })`}</span>
                          . You will be able to use Testfuse normally till then.
                          Any overage acrued during this time will be charged at
                          the end of this period. You will be able to
                          resubscribe after cancellation.
                        </span>
                      </div>
                    ) : (
                      <div
                        className="d-flex flex-row"
                        style={{ marginTop: "32px", gap: "16px" }}
                      >
                        {auth?.data?.tenant_status !== "canceled" &&
                          auth?.data?.tenant_status !== "free" && (
                            <Button
                              btn="ragular-btn"
                              varrient="secondary-btn"
                              onClick={() => {
                                setOpenCancelPlanDialogue(true);
                              }}
                              style={{ height: "50px", width: "210px" }}
                              title={"Cancel Subscription"}
                            />
                          )}
                        <Button
                          btn="ragular-btn"
                          varrient="primary"
                          onClick={() => {
                            setOpenPlansDialogue(true);
                          }}
                          style={{ height: "50px", width: "150px" }}
                          title={
                            auth?.data?.tenant_status === "canceled" ||
                            auth?.data?.tenant_status === "free"
                              ? "Select Plan"
                              : "Change Plan"
                          }
                        />
                      </div>
                    )}
                  </div>
                  <div className="billing-settings-payment">
                    <div className="payment-head">
                      <span className="headline-small semibold">
                        Payment Methods
                      </span>
                    </div>
                    <Elements
                      stripe={stripePromise}
                      options={{
                        fonts: [
                          {
                            cssSrc:
                              "https://fonts.googleapis.com/css?family=Roboto:300,300i,400,500,600",
                          },
                        ],
                      }}
                    >
                      <BillingSettingsAddCardModal
                        addCard={
                          subscription.user_subscription?.length > 0
                            ? true
                            : false
                        }
                        setIsAddCardModalOpen={setIsAddCardModalOpen}
                        setTrigerEvent={setTrigerEvent}
                        trigerEvent={trigerEvent}
                        setLoader={setLoader}
                        open={isAddCardModalOpen}
                        plan={plan}
                      />
                    </Elements>
                    {subscription.payment_methods?.data?.length > 0 ? (
                      <div
                        className="d-flex flex-column"
                        style={{ gap: "16px" }}
                      >
                        {subscription.payment_methods?.data
                          ?.slice(0, 2)
                          .map((payment_method, index) => {
                            return (
                              <div
                                key={index}
                                className="payment-card-detail"
                                style={{ padding: "8px 18px" }}
                              >
                                <div className="payment-card-option">
                                  <Radio
                                    onChangeRadioOption={async () => {
                                      await dispatch(
                                        updateDefaultPaymentMethod({
                                          payment_method_id: payment_method.id,
                                        }),
                                      );
                                      await dispatch(getPaymentMethods());
                                    }}
                                    checked={
                                      payment_method.id ===
                                      payment_method?.customer?.invoice_settings
                                        .default_payment_method
                                    }
                                    varrient="radio-with-custom-component"
                                  />
                                  <div className="payment-card-compny">
                                    <VisaIcon />
                                    <div className="payment-card-owner-detail">
                                      <span className="body-2">
                                        {payment_method.billing_details.name}
                                      </span>
                                      <span
                                        className="body-2"
                                        style={{ color: "#96999C" }}
                                      >
                                        **** **** {payment_method?.card?.last4}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <span className="body-2 dark-50">
                                  Card expires on:{" "}
                                  {`${
                                    payment_method?.card?.exp_month > 9
                                      ? ""
                                      : "0"
                                  }${payment_method?.card?.exp_month}`}
                                  /
                                  {payment_method?.card?.exp_year
                                    ?.toString()
                                    .slice(-2)}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <div className="payment-card-detail">
                        <span className="body-3" style={{ marginLeft: "16px" }}>
                          Your card information will be displayed here...
                        </span>
                        <CustomTooltip
                          text={`If you're Trialing, adding a payment method will end the trial and will mark the start of your subscription.`}
                        >
                          <InfoIcon />
                        </CustomTooltip>
                      </div>
                    )}
                    <span
                      className="body-2 dark-100 pointer"
                      style={{ marginLeft: "5px", textDecoration: "underline" }}
                      onClick={() => {
                        setIsViewCardModalOpen(true);
                      }}
                    >
                      View All
                    </span>
                    <div className="add-card-button">
                      <Button
                        btn="ragular-btn"
                        varrient="primary"
                        disabled={
                          subscription.payment_methods?.data?.length === 5
                        }
                        title={"Add Card"}
                        style={{ height: "50px", width: "150px" }}
                        onClick={() => {
                          setIsAddCardModalOpen(true);
                        }}
                      />
                    </div>
                    <BillingSettingsViewCardModal
                      open={isViewCardModalOpen}
                      data={subscription?.payment_methods?.data}
                      processing={subscription?.is_loading}
                      handleCloseModal={() => {
                        setIsViewCardModalOpen(false);
                      }}
                    />
                  </div>
                </div>

                <div
                  className="w-100"
                  style={{
                    minHeight: "calc(100vh - 515px)",
                    borderRadius: "24px",
                    background: "white",
                  }}
                >
                  {subscription.invoices?.data === null ||
                  !subscription.invoices?.data?.length ? (
                    <div className="default-assessment-card-container">
                      <div
                        className="default-assessment-text"
                        style={{ marginTop: "24px" }}
                      >
                        <span className="body-1 dark-100">
                          Oops! there’s no payment history to display in this
                          table
                        </span>
                      </div>
                    </div>
                  ) : (
                    <Table
                      headers={billingTableHeader}
                      data={rowsData}
                      itemsPerPage={itemsPerPage}
                      nRowsPerPageChange={handleRowsPerPageChange}
                      disableSearchInput={true}
                      customSorting={customSorting}
                      nullTableTitle={"No Payment History"}
                      nullTableDescription={`Oops! there’s no payment history to display in this table`}
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
